import type { ReactNode } from 'react';
import type { Editor } from '@tiptap/react';
import {
  FiBold,
  FiCode,
  FiHash,
  FiItalic,
  FiList,
  FiMinus,
  FiMessageSquare,
  FiRotateCcw,
  FiRotateCw,
  FiType,
  FiUnderline,
} from 'react-icons/fi';
import { richTextToolbarStyles } from '@/shared/ui/rich-text-editor/styles';
import { ToolbarButton, toolbarIconClass } from './toolbar-controls';

type ControlProps = { editor: Editor; isDisabled: boolean };

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className={richTextToolbarStyles.group}>
      {children}
    </div>
  );
}

/** Undo and redo. Disabled when the history has nothing to move through. */
export function HistoryControls({ editor, isDisabled }: ControlProps) {
  return (
    <Group label="History">
      <ToolbarButton
        label="Undo"
        disabled={isDisabled || !editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <FiRotateCcw className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Redo"
        disabled={isDisabled || !editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <FiRotateCw className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
    </Group>
  );
}

/** Inline marks. Each is a toggle, so each reports `aria-pressed`. */
export function MarksControls({ editor, isDisabled }: ControlProps) {
  return (
    <Group label="Text formatting">
      <ToolbarButton
        label="Bold"
        pressed={editor.isActive('bold')}
        disabled={isDisabled || !editor.can().toggleBold()}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FiBold className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        pressed={editor.isActive('italic')}
        disabled={isDisabled || !editor.can().toggleItalic()}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FiItalic className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        pressed={editor.isActive('underline')}
        disabled={isDisabled || !editor.can().toggleUnderline()}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <FiUnderline className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Strikethrough"
        pressed={editor.isActive('strike')}
        disabled={isDisabled || !editor.can().toggleStrike()}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        {/* Feather has no strikethrough glyph, and a foreign icon set is banned.
            A struck-through S is the mark itself; the name is on the button. */}
        <span className="line-through" aria-hidden="true">
          S
        </span>
      </ToolbarButton>
      <ToolbarButton
        label="Inline code"
        pressed={editor.isActive('code')}
        disabled={isDisabled || !editor.can().toggleCode()}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <FiCode className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Clear formatting"
        disabled={isDisabled || !editor.can().unsetAllMarks()}
        onClick={() => editor.chain().focus().unsetAllMarks().run()}
      >
        <FiType className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
    </Group>
  );
}

/** Bullet and ordered lists. */
export function ListControls({ editor, isDisabled }: ControlProps) {
  return (
    <Group label="Lists">
      <ToolbarButton
        label="Bullet list"
        pressed={editor.isActive('bulletList')}
        disabled={isDisabled || !editor.can().toggleBulletList()}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FiList className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        pressed={editor.isActive('orderedList')}
        disabled={isDisabled || !editor.can().toggleOrderedList()}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        {/* Feather has no ordered-list glyph; the number sign carries the same
            meaning and the accessible name is explicit. */}
        <FiHash className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
    </Group>
  );
}

/** Block-level structure. */
export function BlockControls({ editor, isDisabled }: ControlProps) {
  return (
    <Group label="Blocks">
      <ToolbarButton
        label="Quote"
        pressed={editor.isActive('blockquote')}
        disabled={isDisabled || !editor.can().toggleBlockquote()}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <FiMessageSquare className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="Horizontal rule"
        disabled={isDisabled || !editor.can().setHorizontalRule()}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <FiMinus className={toolbarIconClass} aria-hidden="true" />
      </ToolbarButton>
    </Group>
  );
}
